import { useState, useCallback } from 'react'
import axios from 'axios'
import { useQuery } from '@tanstack/react-query'
import { API_BASE_URL } from '../constants/system'
import { SharedDashboardResponse, Dashboard } from '../types/gotypes'

export const useSharedDashboard = (shareId: string) => {
  const [verifiedDashboard, setVerifiedDashboard] = useState<Dashboard | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [verifyError, setVerifyError] = useState<string | null>(null)

  const query = useQuery({
    queryKey: ['shared-dashboard', shareId],
    enabled: Boolean(shareId),
    queryFn: async (): Promise<SharedDashboardResponse> => {
      const res = await axios.get<SharedDashboardResponse>(
        `${API_BASE_URL}/shared/dashboard/${shareId}`
      )
      return res.data
    },
  })

  const verifyPassword = useCallback(async (password: string) => {
    setVerifying(true)
    setVerifyError(null)
    try {
      const res = await axios.post<SharedDashboardResponse>(
        `${API_BASE_URL}/shared/dashboard/${shareId}/verify`,
        { password }
      )
      if (res.data.dashboard) {
        setVerifiedDashboard(res.data.dashboard)
      }
    } catch (err: any) {
      const message = err?.response?.data?.error || 'Incorrect password'
      setVerifyError(message)
    } finally {
      setVerifying(false)
    }
  }, [shareId])

  // The dashboard to display: either from public access or from password verification
  const dashboard = verifiedDashboard || query.data?.dashboard || null

  return {
    data: query.data,
    dashboard,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,

    verifyPassword,
    verifying,
    verifyError,
  }
}

export default useSharedDashboard
